import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import Layout from "@/components/layout/Layout";
import SeoHead from "@/components/SeoHead";
import NewHeroSection from "@/components/home/NewHeroSection";
import WhyYardIsntThrivingSection from "@/components/home/WhyYardIsntThrivingSection";
import EcosystemTrustSection from "@/components/home/EcosystemTrustSection";
import WildlifeEcosystemStrip from "@/components/home/WildlifeEcosystemStrip";
import EcosystemBeforeAfterSection from "@/components/home/EcosystemBeforeAfterSection";

const FloatingHopper = lazy(() => import("@/components/FloatingHopper"));

const Home = () => {
  return (
    <Layout>
      <SeoHead
        title="Frog Knows Natives — Native Plant Plans for Your Yard"
        description="A friendly native garden planner. Get plant recommendations matched to your ecoregion, soil, sun, and USDA zone — with bloom timing and a sourcing guide."
        path="/"
      />
      {/* S1: Hero */}
      <NewHeroSection />

      {/* S1.5: Why Your Yard Isn't Thriving */}
      <WhyYardIsntThrivingSection />

      {/* S2: Ecosystem Trust */}
      <EcosystemTrustSection />

      {/* S2.5: Wildlife Ecosystem */}
      <WildlifeEcosystemStrip />

      {/* S2.7: Before / After Ecosystem */}
      <EcosystemBeforeAfterSection />

      {/* S3: Teaser strip — links to dedicated pages */}
      <section className="py-12 md:py-16 border-t border-border/30">
        <div className="container max-w-3xl text-center">
          <div className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/how-it-works"
              className="group p-6 rounded-2xl bg-card border border-border/40 hover:border-primary/40 transition-colors text-left"
            >
              <p className="font-display text-base font-semibold mb-1">How It Works</p>
              <p className="text-sm text-muted-foreground">
                Three quick steps · Takes less than a minute
              </p>
              <span className="text-sm font-medium text-primary inline-flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">
                See how <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
            <Link
              to="/what-you-get"
              className="group p-6 rounded-2xl bg-card border border-border/40 hover:border-primary/40 transition-colors text-left"
            >
              <p className="font-display text-base font-semibold mb-1">What You Get</p>
              <p className="text-sm text-muted-foreground">
                Plants that thrive, exact placement, and a clear plan.
              </p>
              <span className="text-sm font-medium text-primary inline-flex items-center gap-1 mt-3 group-hover:gap-2 transition-all">
                See what's included <ArrowRight className="h-4 w-4" />
              </span>
            </Link>
          </div>
        </div>
      </section>

      {/* S6: Final CTA */}
      <section className="py-20 md:py-28">
        <div className="container max-w-xl text-center">
          <h2 className="font-display text-2xl md:text-3xl font-bold mb-3">
            Your yard is unique — your plan should be too.
          </h2>
          <p className="text-muted-foreground mb-8">
            No guesswork. No wasted seasons. Just a plan that works.
          </p>
          <Link to="/create" aria-label="Start my native plant plan">
            <Button variant="hero" size="xl" className="gap-2 min-h-[52px]">
              Start My Plan
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Button>
          </Link>
          <p className="text-xs text-muted-foreground mt-3">
            Free to start · Takes less than a minute · No experience needed
          </p>
        </div>
      </section>

      <Suspense fallback={null}>
        <FloatingHopper />
      </Suspense>
    </Layout>
  );
};

export default Home;
